import OpenAI, { toFile } from "openai";
import fs from "fs";
import path from "path";
import { db } from "@workspace/db";
import { sql } from "drizzle-orm";

let _client: OpenAI | null = null;
let _clientKey: string | null = null;

async function getApiKey(): Promise<string | null> {
  try {
    const rows = await db.execute(sql`SELECT value FROM platform_settings WHERE key = 'ai_api_key'`);
    return (rows.rows?.[0] as any)?.value ?? null;
  } catch { return null; }
}

async function getConfiguredModel(): Promise<string> {
  try {
    const rows = await db.execute(sql`SELECT value FROM platform_settings WHERE key = 'ai_model'`);
    return (rows.rows?.[0] as any)?.value ?? "gpt-4.1";
  } catch { return "gpt-4.1"; }
}

async function getClient(): Promise<OpenAI> {
  const dbKey = await getApiKey();
  const key = dbKey || process.env.OPENAI_API_KEY;
  if (!key) throw new Error("OPENAI_API_KEY is required for vector store operations");
  if (_client && _clientKey === key) return _client;
  _client = new OpenAI({ apiKey: key });
  _clientKey = key;
  return _client;
}

export async function ensureTenderVectorStore(
  tenderId: number,
  tenderTitle: string,
  existingVsId?: string | null,
): Promise<string> {
  const client = await getClient();
  if (existingVsId) {
    try {
      await client.vectorStores.retrieve(existingVsId);
      return existingVsId;
    } catch { /* create a new one below */ }
  }
  const vs = await client.vectorStores.create({
    name: `Tender-${tenderId}: ${tenderTitle.slice(0, 60)}`,
  });
  return vs.id;
}

export async function uploadFileToVectorStore(
  vectorStoreId: string,
  filePath: string,
  fileName: string,
): Promise<string> {
  const client = await getClient();
  const fileBuffer = fs.readFileSync(filePath);
  const ext = path.extname(fileName).toLowerCase();
  const mimeType = ext === ".pdf" ? "application/pdf"
    : ext === ".docx" ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    : "application/octet-stream";

  const uploadedFile = await client.files.create({
    file: await toFile(fileBuffer, fileName, { type: mimeType }),
    purpose: "assistants",
  });

  await client.vectorStores.files.create(vectorStoreId, { file_id: uploadedFile.id });

  // Poll until indexed (max 90s)
  let waited = 0;
  while (waited < 90000) {
    try {
      const vsFile = await client.vectorStores.files.retrieve(vectorStoreId, uploadedFile.id);
      if (vsFile.status === "completed" || vsFile.status === "failed") break;
    } catch { break; }
    await new Promise((r) => setTimeout(r, 2500));
    waited += 2500;
  }

  return uploadedFile.id;
}

export async function deleteFileFromVectorStore(
  vectorStoreId: string,
  openaiFileId: string,
): Promise<void> {
  const client = await getClient();
  try { await client.vectorStores.files.del(vectorStoreId, openaiFileId); } catch { }
  try { await client.files.del(openaiFileId); } catch { }
}

export async function chatWithVectorStore(
  vectorStoreId: string,
  messages: Array<{ role: "user" | "assistant"; content: string }>,
  language: "ar" | "en",
  tenderTitle: string,
): Promise<string> {
  const client = await getClient();
  const model = await getConfiguredModel();

  const systemContent = language === "ar"
    ? `أنت مساعد ذكي متخصص في تحليل المناقصات الحكومية السعودية. يمكنك البحث مباشرةً في وثائق مناقصة "${tenderTitle}" عبر أداة البحث المدمجة. استند إلى محتوى الوثائق الفعلية في إجاباتك وأشر إلى المصدر عند الاقتباس. أجب باللغة العربية بأسلوب احترافي.`
    : `You are an AI assistant specialized in Saudi government tender analysis. You can directly search the documents of tender "${tenderTitle}" using the built-in file search tool. Base your answers on the actual document content and cite sources when quoting. Reply professionally in English.`;

  const response = await (client as any).responses.create({
    model,
    input: [
      { role: "developer", content: systemContent },
      ...messages.map((m) => ({ role: m.role, content: m.content })),
    ],
    tools: [{ type: "file_search", vector_store_ids: [vectorStoreId] }],
  });

  // Extract text — support both output_text shortcut and manual traversal
  const text: string = response.output_text
    ?? response.output
      ?.filter((o: any) => o.type === "message")
      ?.flatMap((o: any) =>
        (o.content ?? [])
          .filter((c: any) => c.type === "output_text" || c.type === "text")
          .map((c: any) => c.text ?? c.value ?? ""),
      )
      ?.join("") ?? "";

  return text;
}
